import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query"
import { TRPCClientError } from "@trpc/client"
import { trpc } from "#/lib/trpc/client"
import { trpcRetry } from "#/lib/trpc/retryPolicy"

const MEMBER_PROGRAM_QUERY_KEY = "GetMemberProgram"

type MemberProgram = Awaited<ReturnType<typeof trpc.member.program.query>>

// Everything a caller may pass except the parts the hook owns.
type MemberProgramOptions<TData> = Omit<
  UseQueryOptions<MemberProgram, TRPCClientError<never>, TData, [string]>,
  "queryKey" | "queryFn"
>

export function useMemberProgram<TData = MemberProgram>(
  options?: MemberProgramOptions<TData>
): UseQueryResult<TData, TRPCClientError<never>> {
  return useQuery<MemberProgram, TRPCClientError<never>, TData, [string]>({
    queryKey: [MEMBER_PROGRAM_QUERY_KEY],
    queryFn: () => trpc.member.program.query(),
    refetchOnWindowFocus: false,
    retry: trpcRetry(1),
    // Reported from the query, not from each consumer: several components read this at once
    // (navigation, the user dropdown, the paywall gate) and a per-consumer effect would turn
    // one failure into one Sentry event per reader. `handleQueryError` skips 401s before it
    // gets here, so a routine session expiry does not report.
    meta: {
      errorReport: {
        message: "member.program query failed",
        tags: { query: "member.program" }
      }
    },
    ...options
  })
}

export type MemberProgramFlags = {
  isConsumer: boolean
  isEngage: boolean
}

// Module-scoped so the query observer can memoize the transform by identity
// (it only reuses the previous select result when the select reference is
// stable); an inline closure would re-run this on every render instead.
const selectMemberProgramFlags = (data: MemberProgram): MemberProgramFlags => ({
  isConsumer: data.programType === "CONSUMER",
  isEngage: data.programType === "WTW_ENGAGE"
})

// Derived view of the member program used for branching on program type.
// Single source of truth for these flags so call sites don't each re-derive
// the program-type string comparisons.
export function useMemberProgramFlags(enabled = true): UseQueryResult<MemberProgramFlags, TRPCClientError<never>> {
  return useMemberProgram({ select: selectMemberProgramFlags, enabled })
}
